import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useToast } from '@/components/ui/Toast';

/**
 * Menghubungkan aplikasi ke aliran progres worker.
 *
 * Ini yang mengubah status "Berjalan" dari label statis pada rancangan menjadi
 * angka yang benar-benar bergerak. Saat sebuah run selesai, cache TanStack
 * Query yang relevan ditandai basi sehingga daftar dan kartu statistik
 * menyegarkan dirinya sendiri tanpa pengguna perlu memuat ulang halaman.
 */
export const useRealtime = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const organizationId = useAuthStore((s) => s.organization?.id);
  const socketRef = useRef(null);

  useEffect(() => {
    const token = getAccessToken();
    if (!token || !organizationId) return undefined;

    const socket = io({
      path: '/socket.io',
      auth: { token, organizationId },
      // Mulai dengan polling lalu naik ke WebSocket. Membuka WebSocket lebih
      // dulu gagal di balik proxy dev Vite; urutan ini bekerja di keduanya
      // dan tetap berakhir pada WebSocket setelah upgrade.
      transports: ['polling', 'websocket'],
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 10_000,
    });

    socket.on('connect_error', (err) => {
      // Kegagalan realtime tidak fatal - halaman tetap menyegarkan lewat
      // polling TanStack Query, jadi cukup dicatat.
      console.warn('Koneksi realtime gagal:', err.message);
    });
    socketRef.current = socket;

    socket.on('run:progress', (payload) => {
      // Progres ditulis langsung ke cache agar UI bergerak halus tanpa
      // menembak ulang API setiap beberapa ratus milidetik.
      queryClient.setQueryData(['run', payload.runId], (previous) =>
        previous
          ? {
              ...previous,
              data: {
                ...previous.data,
                status: 'running',
                progress: { ...previous.data.progress, ...payload },
              },
            }
          : previous,
      );
    });

const invalidate = (queryClient, runId) => {
  queryClient.invalidateQueries({ queryKey: ['run', runId] });
  queryClient.invalidateQueries({ queryKey: ['jobs'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  queryClient.invalidateQueries({ queryKey: ['notifications'] });
};

    socket.on('run:completed', (payload) => {
      toast.success('Crawl selesai', {
        description: `${(payload.recordsExtracted ?? 0).toLocaleString('id-ID')} baris data berhasil diambil.`,
      });
      invalidate(queryClient, payload.runId);
    });

    socket.on('run:failed', (payload) => {
      toast.error('Crawl gagal', {
        description: payload.error ?? 'Buka detail run untuk melihat penyebabnya.',
      });
      invalidate(queryClient, payload.runId);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
    // `toast` stabil dari context; sengaja tidak masuk dependensi agar koneksi
    // tidak dibangun ulang setiap render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, queryClient]);

  return socketRef;
};


/** Memantau satu run secara khusus, dipakai halaman detail run. */
export const useWatchRun = (socketRef, runId) => {
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket || !runId) return undefined;

    socket.emit('run:watch', runId);
    return () => socket.emit('run:unwatch', runId);
  }, [socketRef, runId]);
};
